import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportRowsToCsv } from '../utils/comparisonUtils';

const METRICS = [
  { value: 'CRR', label: 'CPR' },
  { value: 'CDR', label: 'CDR' },
  { value: 'SEV', label: 'SEV' },
  { value: 'DLNQ60', label: 'DLNQ' },
];

const SERIES_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#ca8a04',
  '#db2777',
];

const rowTranche = (row) => row?.TRANCHE || row?.Tranche || row?.tranche || '';
const rowCusip = (row) => row?.CUSIP || row?.Cusip || row?.cusip || '';

const normalizeDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const raw = String(value);
  if (/^\d{6}$/.test(raw)) {
    const year = raw.slice(0, 4);
    const month = raw.slice(4, 6);
    return {
      key: `${year}-${month}-01`,
      label: `${month}/01/${year}`,
      sortValue: Number(raw),
    };
  }

  const parsed = dayjs(value);
  if (parsed.isValid()) {
    return {
      key: parsed.format('YYYY-MM-DD'),
      label: parsed.format('MM/DD/YYYY'),
      sortValue: parsed.valueOf(),
    };
  }

  return { key: raw, label: raw, sortValue: raw };
};

const AdcoComparisonChart = memo(({
  scenarios,
  tranches = [],
  height = 400,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedTranche, setSelectedTranche] = useState('');
  const [selectedCusip, setSelectedCusip] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('CRR');

  const series = useMemo(() => Object.entries(scenarios || {}).flatMap(
    ([label, scenario], index) => {
      const rows = Array.isArray(scenario) ? scenario : scenario?.adco;
      if (!Array.isArray(rows) || rows.length === 0) return [];
      return [{
        label,
        key: `scenario_${index}`,
        rows,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
      }];
    }
  ), [scenarios]);

  const availableTranches = useMemo(() => {
    const result = [];
    const seen = new Set();
    const add = (tranche) => {
      if (!tranche || seen.has(tranche)) return;
      seen.add(tranche);
      result.push(tranche);
    };

    tranches.forEach(add);
    series.forEach(({ rows }) => rows.forEach(row => add(rowTranche(row))));
    return result;
  }, [series, tranches]);

  useEffect(() => {
    if (!availableTranches.includes(selectedTranche)) {
      setSelectedTranche(availableTranches[0] || '');
    }
  }, [availableTranches, selectedTranche]);

  const availableCusips = useMemo(() => {
    const result = [];
    const seen = new Set();

    series.forEach(({ rows }) => rows.forEach((row) => {
      const cusip = rowCusip(row);
      if (rowTranche(row) !== selectedTranche || !cusip || seen.has(cusip)) return;
      seen.add(cusip);
      result.push(cusip);
    }));

    return result;
  }, [selectedTranche, series]);

  useEffect(() => {
    if (availableCusips.length === 0) {
      if (selectedCusip) setSelectedCusip('');
      return;
    }

    if (!availableCusips.includes(selectedCusip)) {
      setSelectedCusip(availableCusips[0]);
    }
  }, [availableCusips, selectedCusip]);

  const chartData = useMemo(() => {
    if (!selectedTranche) return [];

    const points = new Map();

    series.forEach(({ key, rows }) => {
      const valuesByDate = new Map();

      rows.forEach(row => {
        if (rowTranche(row) !== selectedTranche) return;
        if (availableCusips.length > 0 && rowCusip(row) !== selectedCusip) return;

        const numericValue = Number(row?.[selectedMetric]);
        const date = normalizeDate(row?.DATE);
        if (!date || !Number.isFinite(numericValue)) return;

        const aggregate = valuesByDate.get(date.key) || { ...date, total: 0, count: 0 };
        aggregate.total += numericValue;
        aggregate.count += 1;
        valuesByDate.set(date.key, aggregate);
      });

      valuesByDate.forEach(value => {
        const point = points.get(value.key) || {
          date: value.key,
          dateFormatted: value.label,
          sortValue: value.sortValue,
        };
        point[key] = value.total / value.count;
        points.set(value.key, point);
      });
    });

    return Array.from(points.values()).sort((a, b) => {
      if (typeof a.sortValue === 'number' && typeof b.sortValue === 'number') {
        return a.sortValue - b.sortValue;
      }
      return String(a.sortValue).localeCompare(String(b.sortValue));
    });
  }, [availableCusips, selectedCusip, selectedMetric, selectedTranche, series]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, mb: 2 }}>
        <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
          <InputLabel id="adco-tranche-select-label">Tranche</InputLabel>
          <Select
            labelId="adco-tranche-select-label"
            id="adco-tranche-select"
            value={selectedTranche}
            onChange={(event) => setSelectedTranche(event.target.value)}
            label="Tranche"
            sx={{ bgcolor: theme.palette.background.paper }}
          >
            {availableTranches.map(tranche => (
              <MenuItem key={tranche} value={tranche}>{tranche}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {availableCusips.length > 1 && (
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
            <InputLabel id="adco-cusip-select-label">CUSIP</InputLabel>
            <Select
              labelId="adco-cusip-select-label"
              id="adco-cusip-select"
              value={selectedCusip}
              onChange={(event) => setSelectedCusip(event.target.value)}
              label="CUSIP"
              sx={{ bgcolor: theme.palette.background.paper }}
            >
              {availableCusips.map(cusip => (
                <MenuItem key={cusip} value={cusip}>{cusip}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {availableCusips.length === 1 && (
          <Chip label={`CUSIP ${availableCusips[0]}`} size="small" variant="outlined" />
        )}

        <ToggleButtonGroup
            size="small"
            exclusive
            value={selectedMetric}
            onChange={(_, value) => value && setSelectedMetric(value)}
            aria-label="ADCO metric"
            sx={{ ml: { sm: 'auto' } }}
          >
            {METRICS.map(metric => (
              <ToggleButton
                key={metric.value}
                value={metric.value}
                aria-label={metric.label}
              >
                {metric.label}
              </ToggleButton>
            ))}
        </ToggleButtonGroup>
        <Button
          size="small"
          variant="outlined"
          disabled={chartData.length === 0}
          onClick={() => exportRowsToCsv(
            chartData.map(row => ({
              DATE: row.dateFormatted,
              ...Object.fromEntries(series.map(item => [item.label, row[item.key]])),
            })),
            `adco_chart_${selectedMetric}`
          )}
        >
          Export CSV
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2.5,
          minHeight: height,
        }}
      >
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDark ? theme.palette.divider : '#e2e8f0'}
              />
              <XAxis
                dataKey="dateFormatted"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => Number(value).toFixed(2)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  color: theme.palette.text.primary,
                }}
                formatter={(value, name) => [Number(value).toFixed(4), name]}
              />
              <Legend wrapperStyle={{ color: theme.palette.text.primary }} />
              {series.map(item => (
                <Line
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stroke={item.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
              {chartData.length > 1 && (
                <Brush
                  dataKey="dateFormatted"
                  height={22}
                  travellerWidth={8}
                  stroke={theme.palette.primary.main}
                  fill={theme.palette.background.paper}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height,
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="body1">
              No data available for the selected tranche and metric
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
});

AdcoComparisonChart.displayName = 'AdcoComparisonChart';

export default AdcoComparisonChart;
